import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { confirmable, createConfirmation } from 'react-confirm';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import _ from 'lodash';
import { Button } from '~/components/ui/button';
import { DialogOverlay, DialogPortal, MODAL_OVERLAY_STYLE } from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { ModalTitle } from '../modal';

// Legacy variant names (still passed by callers) mapped to the shadcn Button
const BUTTON_VARIANTS = {
	primary: 'default',
	secondary: 'secondary',
	warning: 'destructive',
	danger: 'destructive',
	'ghost-primary': 'ghost'
};

const ConfirmModal = ({
						  show,
						  proceed,
						  title,
						  content,
						  cancelText,
						  confirmText,
						  cancelVariant = 'secondary',
						  confirmVariant = 'primary',
						  textField
}) => {

	const [textFieldValue, setTextFieldValue] = useState('');

	return (
		<DialogPrimitive.Root open={show} onOpenChange={(open) => !open && proceed(false)}>
			<DialogPortal>
				{/* Same backdrop as the global Modal; stays on the shadcn Dialog z-index (201)
				so confirms surface above any open modal or dialog */}
				<DialogOverlay style={MODAL_OVERLAY_STYLE} />
				<DialogPrimitive.Content
					className="fixed left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 overflow-auto rounded bg-card text-card-foreground focus:outline-none"
					style={{
						width: 'min(600px, calc(100vw - 3.5rem))',
						maxHeight: 'calc(100% - 3.5rem)',
						zIndex: 201
					}}
					aria-label={title}
					aria-describedby={undefined}
					onInteractOutside={(event) => event.preventDefault()}
					onEscapeKeyDown={(event) => {
						event.preventDefault();
						proceed(false);
					}}
				>
					<header className="px-8 pt-6">
						<DialogPrimitive.Title asChild>
							<ModalTitle>{title}</ModalTitle>
						</DialogPrimitive.Title>
					</header>
					<div className="px-8 pt-4 pb-8 space-y-4">
						<span className="block">{content}</span>
						{textField && <Input
							type="text"
							name="confirmMessage"
							id="confirmMessage"
							value={textFieldValue}
							onChange={(e) => setTextFieldValue(e.target.value)}
						/>
						}
					</div>

					<footer className="flex items-center justify-end gap-2 px-8 pb-7">
						<Button
							variant={BUTTON_VARIANTS[cancelVariant]}
							onClick={() => proceed(false)}
							data-testid="confirm-cancel"
						>
							{cancelText}
						</Button>
						<Button
							variant={BUTTON_VARIANTS[confirmVariant]}
							disabled={(textField && _.isEmpty(textFieldValue)) || false}
							onClick={() => proceed( textField ? textFieldValue : true)}
							data-testid="confirm-ok"
						>
							{confirmText}
						</Button>
					</footer>
				</DialogPrimitive.Content>
			</DialogPortal>
		</DialogPrimitive.Root>
	);

};

ConfirmModal.propTypes = {
	show: PropTypes.bool,
	proceed: PropTypes.func,
	title: PropTypes.string,
	textField: PropTypes.bool,
	content: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.element
	]),
	cancelText: PropTypes.string,
	confirmText: PropTypes.string,
	cancelVariant: PropTypes.oneOf([
		'primary', 'secondary', 'warning', 'danger', 'ghost-primary'
	]),
	confirmVariant: PropTypes.oneOf([
		'primary', 'secondary', 'warning', 'danger', 'ghost-primary'
	])
};

export default createConfirmation(confirmable(ConfirmModal));
